import { z } from "zod";
import { countries, type TCountryCode, type TCurrencyCode } from "countries-list";
import { currencies } from "countries-list/currencies";

export const MONEY_MAX_PAISE = 100_000_000_000;

export const MoneyPaiseSchema = z.number().int().min(0).max(MONEY_MAX_PAISE);
export type MoneyPaise = z.infer<typeof MoneyPaiseSchema>;

export type SupportedCountryCode = TCountryCode | "OTHER";
export const SupportedCountryCodeSchema = z.custom<SupportedCountryCode>((value) =>
  value === "OTHER" || (typeof value === "string" && Object.prototype.hasOwnProperty.call(countries, value))
, "Choose a recognised country.");
export type CurrencyCode = TCurrencyCode;
export const CurrencyCodeSchema = z.custom<CurrencyCode>((value) =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(currencies, value) && !currencies[value as CurrencyCode].withdrawn
, "Choose a current currency.");
export type CountryPackStatus = "reviewed-local" | "global-basics";

export interface CountryOption {
  code: SupportedCountryCode;
  label: string;
  currency: CurrencyCode;
  locale: string;
  packStatus: CountryPackStatus;
}

function localeForCountry(code: TCountryCode): string {
  const candidate = `en-${code}`;
  try {
    new Intl.NumberFormat(candidate);
    return candidate;
  } catch {
    return "en";
  }
}

function defaultCurrencyForCountry(code: TCountryCode): CurrencyCode {
  return countries[code].currency.find((currency) => !currencies[currency].withdrawn) ?? "USD";
}

export const COUNTRY_OPTIONS: readonly CountryOption[] = [
  ...Object.entries(countries)
    .map(([code, country]) => ({
      code: code as TCountryCode,
      label: country.name,
      currency: defaultCurrencyForCountry(code as TCountryCode),
      locale: localeForCountry(code as TCountryCode),
      packStatus: code === "IN" ? "reviewed-local" as const : "global-basics" as const
    }))
    .sort((left, right) => left.label.localeCompare(right.label)),
  { code: "OTHER", label: "Another country or territory", currency: "USD", locale: "en", packStatus: "global-basics" }
] as const;

export interface CurrencyOption {
  code: CurrencyCode;
  label: string;
}

const usedCurrencyCodes = new Set<CurrencyCode>(
  Object.values(countries).flatMap((country) => country.currency.filter((currency) => !currencies[currency].withdrawn))
);
usedCurrencyCodes.add("USD");

export const CURRENCY_OPTIONS: readonly CurrencyOption[] = [...usedCurrencyCodes]
  .map((code) => ({ code, label: currencies[code].name }))
  .sort((left, right) => left.label.localeCompare(right.label));

export function countryOption(code: SupportedCountryCode): CountryOption {
  return COUNTRY_OPTIONS.find((option) => option.code === code) ?? COUNTRY_OPTIONS[COUNTRY_OPTIONS.length - 1]!;
}

export const CountrySelectionSchema = z.object({
  country: SupportedCountryCodeSchema,
  currency: CurrencyCodeSchema,
  timezone: z.string().trim().min(1).max(80).optional()
});
export type CountrySelection = z.infer<typeof CountrySelectionSchema>;

export const AgeBandSchema = z.enum(["18-24", "25-34", "35-44", "45-54", "55-64", "65+"]);
export const HouseholdTypeSchema = z.enum(["single", "couple", "family", "joint-family"]);
export const HouseholdRelationshipSchema = z.enum(["self", "partner", "child", "parent", "other"]);
export const HouseholdMemberAgeBandSchema = z.enum(["0-12", "13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65-74", "75+"]);
export const MemberHealthCoverageSchema = z.enum(["covered", "not-covered", "unknown"]);
export const HealthCoverageSourceSchema = z.enum(["public", "employer", "private", "other"]);
export const HouseholdMemberSchema = z.object({
  id: z.string().trim().min(1).max(64),
  relationship: HouseholdRelationshipSchema,
  ageBand: HouseholdMemberAgeBandSchema,
  earnsIncome: z.boolean(),
  financiallyDependent: z.boolean(),
  healthCoverage: MemberHealthCoverageSchema.default("unknown"),
  healthCoverageSources: z.array(HealthCoverageSourceSchema).max(4).optional(),
  healthCoverageEndsWithEmployment: z.boolean().nullable().optional(),
  incomeProtection: MemberHealthCoverageSchema.optional(),
  incomeProtectionAmountPaise: MoneyPaiseSchema.optional()
});
export type HouseholdMember = z.infer<typeof HouseholdMemberSchema>;
export type HealthCoverageSource = z.infer<typeof HealthCoverageSourceSchema>;
export const DependencyConfidenceSchema = z.enum(["confirmed", "estimated"]);
export const HouseholdDependencyLinkSchema = z.object({
  earnerMemberId: z.string().trim().min(1).max(64),
  dependentMemberId: z.string().trim().min(1).max(64),
  confidence: DependencyConfidenceSchema
});
export type HouseholdDependencyLink = z.infer<typeof HouseholdDependencyLinkSchema>;

export const HealthProtectionReviewSchema = z.object({
  deductibleOrCopayReviewed: z.boolean(),
  roomAndSublimitsReviewed: z.boolean(),
  waitingPeriodsReviewed: z.boolean(),
  exclusionsReviewed: z.boolean(),
  networkReviewed: z.boolean(),
  continuityReviewed: z.boolean()
});
export type HealthProtectionReview = z.infer<typeof HealthProtectionReviewSchema>;

export const HealthProtectionDetailsSchema = z.object({
  stressTestAmountPaise: MoneyPaiseSchema.default(0),
  estimatedCostSharePaise: MoneyPaiseSchema.default(0),
  sharedLimit: z.boolean().default(true)
});
export type HealthProtectionDetails = z.infer<typeof HealthProtectionDetailsSchema>;

export const LifeProtectionDetailsSchema = z.object({
  dependantSupportMonths: z.number().int().min(0).max(1_200).default(36),
  immediateCostsPaise: MoneyPaiseSchema,
  educationObligationsPaise: MoneyPaiseSchema,
  otherObligationsPaise: MoneyPaiseSchema,
  availableAssetsPaise: MoneyPaiseSchema,
  survivorIncomeMonthlyPaise: MoneyPaiseSchema,
  survivorIncomeMonths: z.number().int().min(0).max(1_200),
  employerOrPublicBenefitsPaise: MoneyPaiseSchema,
  nomineesAndTermReviewed: z.boolean()
});
export type LifeProtectionDetails = z.infer<typeof LifeProtectionDetailsSchema>;
export const IncomeStabilitySchema = z.enum(["stable", "variable", "seasonal", "between-income"]);
export const HealthCoverSchema = z.enum(["none", "employer", "personal", "government", "multiple", "unknown"]);
export const LifeCoverSchema = z.enum(["none", "employer", "term", "savings-linked", "multiple", "unknown"]);
export const TopConcernSchema = z.enum(["cash-flow", "emergency", "health-cover", "debt", "goals", "investing", "retirement"]);
export const PrimaryGoalSchema = z.enum(["none", "emergency", "home", "education", "retirement", "travel", "debt-free", "other"]);

export const DeepReviewAnswerSchema = z.enum(["reviewed", "needs-attention", "not-applicable", "unknown"]);
export type DeepReviewAnswer = z.infer<typeof DeepReviewAnswerSchema>;
export const ExtendedFinancialReviewSchema = z.object({
  creditAndBorrowing: DeepReviewAnswerSchema.optional(),
  retirementPlanning: DeepReviewAnswerSchema.optional(),
  investingReadiness: DeepReviewAnswerSchema.optional(),
  taxAndBenefits: DeepReviewAnswerSchema.optional(),
  propertyAndLiability: DeepReviewAnswerSchema.optional(),
  estateAndBeneficiaries: DeepReviewAnswerSchema.optional(),
  familyCareTransitions: DeepReviewAnswerSchema.optional(),
  fraudAndDigitalSafety: DeepReviewAnswerSchema.optional(),
  crossBorderMoney: DeepReviewAnswerSchema.optional(),
  recordsAndReviews: DeepReviewAnswerSchema.optional()
});
export type ExtendedFinancialReview = z.infer<typeof ExtendedFinancialReviewSchema>;

export const FinancialFactsSchema = z.object({
  country: SupportedCountryCodeSchema,
  currency: CurrencyCodeSchema.default("INR"),
  state: z.string().trim().min(2).max(80).optional(),
  ageBand: AgeBandSchema,
  householdType: HouseholdTypeSchema,
  dependants: z.number().int().min(0).max(20),
  earners: z.number().int().min(0).max(20),
  incomeMonthlyPaise: MoneyPaiseSchema,
  incomeStability: IncomeStabilitySchema,
  essentialExpensesMonthlyPaise: MoneyPaiseSchema,
  debtPaymentsMonthlyPaise: MoneyPaiseSchema,
  outstandingDebtPaise: MoneyPaiseSchema.default(0),
  liquidCashPaise: MoneyPaiseSchema,
  healthCover: HealthCoverSchema,
  healthCoverAmountPaise: MoneyPaiseSchema.default(0),
  healthProtectionDetails: HealthProtectionDetailsSchema.optional(),
  lifeCover: LifeCoverSchema.default("unknown"),
  lifeCoverAmountPaise: MoneyPaiseSchema.default(0),
  householdMembers: z.array(HouseholdMemberSchema).max(12).default([]),
  dependencyLinks: z.array(HouseholdDependencyLinkSchema).max(132).optional(),
  healthProtectionReview: HealthProtectionReviewSchema.optional(),
  lifeProtectionDetails: LifeProtectionDetailsSchema.optional(),
  hasOverdueDebt: z.boolean().nullable().optional(),
  primaryGoal: PrimaryGoalSchema.default("none"),
  goalTargetPaise: MoneyPaiseSchema.default(0),
  goalCurrentSavingsPaise: MoneyPaiseSchema.default(0),
  goalTargetYears: z.number().int().min(0).max(50).default(0),
  topConcern: TopConcernSchema,
  extendedReview: ExtendedFinancialReviewSchema.optional()
}).superRefine((facts, context) => {
  const earners = facts.householdMembers.length > 0
    ? facts.householdMembers.filter((member) => member.earnsIncome).length
    : facts.earners;
  if (earners === 0 && facts.incomeMonthlyPaise > 0) {
    context.addIssue({
      code: "custom",
      path: ["earners"],
      message: "At least one earner is needed when monthly income is above zero."
    });
  }
});

export type FinancialFacts = z.infer<typeof FinancialFactsSchema>;

export const PillarIdSchema = z.enum(["cash-flow", "safety-buffer", "health-protection", "life-protection", "debt", "goals", "future"]);
export type PillarId = z.infer<typeof PillarIdSchema>;
export type Confidence = "low" | "medium" | "high";
export type Priority = 1 | 2 | 3 | 4;
export type PillarStatus = "urgent" | "attention" | "steady" | "unknown";

export interface SourceReference {
  id: string;
  title: string;
  publisher: string;
  url: string;
  reviewedOn: string;
  appliesTo: SupportedCountryCode | "global";
}

export interface Explainability {
  factsUsed: string[];
  assumptions: string[];
  changesIf: string[];
  sourceIds: string[];
  rulesetVersion: string;
}

export interface PillarResult {
  id: PillarId;
  label: string;
  score: number | null;
  status: PillarStatus;
  confidence: Confidence;
  summary: string;
}

export interface Quest {
  id: string;
  title: string;
  description: string;
  pillar: PillarId;
  priority: Priority;
  effortMinutes: number;
  rewardXp: number;
  successText: string;
  explainability: Explainability;
}

export interface DerivedMetrics {
  monthlyOutgoPaise: MoneyPaise;
  monthlySurplusPaise: number;
  debtPaymentRatioBps: number | null;
  runwayMonthsHundredths: number | null;
  runwayDays: number | null;
  bufferTargetMonths: number;
  bufferTargetPaise: MoneyPaise;
  bufferGapPaise: MoneyPaise;
  healthCostScenarioPaise: MoneyPaise | null;
  healthEstimatedCoverCapacityPaise: MoneyPaise;
  healthEstimatedHouseholdExposurePaise: MoneyPaise | null;
  healthCoverageRatioBps: number | null;
  lifeCoverSupportMonths: number;
  lifeImmediateNeedsPaise: MoneyPaise;
  lifeOngoingNeedsPaise: MoneyPaise;
  lifeAvailableResourcesPaise: MoneyPaise;
  lifeCoverPlanningNeedPaise: MoneyPaise;
  lifeCoverGapPaise: MoneyPaise;
  goalFundingGapPaise: MoneyPaise;
}

export type DecisionPlanState = "monthly_shortfall" | "build_first_month" | "build_resilience" | "cash_resilience_funded";
export interface DecisionPlan {
  state: DecisionPlanState;
  monthlyIncomePaise: MoneyPaise;
  essentialExpensesPaise: MoneyPaise;
  requiredDebtPaymentsPaise: MoneyPaise;
  monthlyCapacityPaise: MoneyPaise;
  monthlyShortfallPaise: MoneyPaise;
  firstBufferTargetPaise: MoneyPaise;
  firstBufferGapPaise: MoneyPaise;
  monthsToFirstBufferAtCurrentCapacity: number | null;
  resilienceTargetPaise: MoneyPaise;
  resilienceGapPaise: MoneyPaise;
  monthsToResilienceAtCurrentCapacity: number | null;
  goalMonthlyCheckpointPaise: MoneyPaise | null;
  goalCapacityGapPaise: MoneyPaise | null;
  topFindingId: string | null;
}

export interface HouseholdSnapshot {
  members: HouseholdMember[];
  dependencyLinks: HouseholdDependencyLink[];
  memberCount: number;
  earnerCount: number;
  dependantCount: number;
  childCount: number;
  seniorParentCount: number;
  uncoveredHealthCount: number;
  unknownHealthCount: number;
  unprotectedEarnerCount: number;
  unknownEarnerProtectionCount: number;
}

export type RiskState = "act_first" | "review_soon" | "protected" | "unknown";
export type RiskSeverity = "critical" | "high" | "moderate" | "low";
export type RiskConfidence = "confirmed" | "estimated" | "unknown";
export type RiskTimeHorizon = "immediate" | "one_year" | "long_term";
export type RiskHazard = "cashflow" | "income_loss" | "health_cost" | "death" | "disability" | "debt" | "goal" | "retirement";
export type OutcomeId = "today" | "shock" | "protect" | "future";

export type CoverageAreaId = "monthly-money" | "emergency-income-shock" | "health-costs" | "life-disability"
  | "debt-credit" | "goals-education-housing" | "retirement" | "investing" | "tax-benefits"
  | "property-liability" | "estate-continuity" | "family-care" | "fraud-security" | "cross-border"
  | "records-reviews";
export type CoverageState = "needs_attention" | "checked" | "self_reviewed" | "not_yet_checked" | "not_applicable" | "local_guidance_needed";

export interface CoverageArea {
  id: CoverageAreaId;
  label: string;
  state: CoverageState;
  source: "fast_scan" | "fast_and_deep_scan" | "deep_scan";
  countryScope: "universal" | "country_dependent";
  summary: string;
  researchTopics: string[];
}

export interface CoverageSummary {
  needsAttention: number;
  checked: number;
  selfReviewed: number;
  notYetChecked: number;
  notApplicable: number;
  localGuidanceNeeded: number;
  total: number;
}

export interface ContentCoverageAudit {
  articleCount: number;
  extractedWordCount: number;
  publisherCount: number;
  topicCount: number;
  completedOn: string;
}

export interface ScenarioValue {
  label: string;
  kind: "money" | "duration" | "count" | "ratio" | "text";
  value: number | string;
  unit?: "currency_minor" | "days" | "months" | "people" | "percent";
}

export interface FactReference {
  key: string;
  label: string;
  value: number | string;
  unit?: "currency_minor" | "days" | "months" | "people" | "percent";
}

export interface MissingFact {
  key: string;
  label: string;
  reason: string;
}

export type ActionEvidenceMode = "fact-updated" | "document-checked" | "real-world-action" | "accepted-risk";

export interface RiskDelta {
  label: string;
  before: number | string;
  after: number | string;
  unit?: "currency_minor" | "days" | "months" | "people" | "percent" | "state";
}

export interface RiskAction {
  questId: string;
  title: string;
  description: string;
  effortMinutes: number;
  successText: string;
  impactLabel: string;
  evidenceModes: ActionEvidenceMode[];
}

export interface RiskFinding {
  id: string;
  pillar: PillarId;
  outcome: OutcomeId;
  hazard: RiskHazard;
  affectedMemberIds: string[];
  state: RiskState;
  severity: RiskSeverity;
  confidence: RiskConfidence;
  timeHorizon: RiskTimeHorizon;
  headline: string;
  explanation: string;
  consequence: ScenarioValue;
  availableCapacity: ScenarioValue;
  gap: ScenarioValue;
  factsUsed: FactReference[];
  missingFacts: MissingFact[];
  assumptions: string[];
  nextAction: RiskAction;
  expectedDelta: RiskDelta;
  sourceIds: string[];
  rulePackVersion: string;
}

export interface RiskSummary {
  actFirst: number;
  reviewSoon: number;
  protected: number;
  unknown: number;
  dataConfidencePercent: number;
  confirmedAreas: number;
  totalAreas: number;
}

export interface OutcomeSummary {
  id: OutcomeId;
  label: string;
  question: string;
  state: RiskState;
  findingIds: string[];
}

export interface RulePackDisclosure {
  schemaVersion: "2";
  reviewedOn: string;
  nextReviewOn: string;
  unsupportedAreas: string[];
}

export interface FinancialPlan {
  id: string;
  country: SupportedCountryCode;
  currency: CurrencyCode;
  localizationStatus: CountryPackStatus;
  engineVersion: string;
  rulesetVersion: string;
  rulePack: RulePackDisclosure;
  generatedAt: string;
  headline: string;
  overallScore?: number;
  household?: HouseholdSnapshot;
  metrics: DerivedMetrics;
  decisionPlan: DecisionPlan;
  pillars: PillarResult[];
  riskSummary: RiskSummary;
  riskFindings: RiskFinding[];
  outcomes: OutcomeSummary[];
  coverageSummary: CoverageSummary;
  coverageAreas: CoverageArea[];
  contentCoverageAudit: ContentCoverageAudit;
  quests: Quest[];
  sourceIds: string[];
  disclaimer: string;
}

export const ProfileSectionSchema = z.enum(["household", "cash-flow", "protection", "debts", "goals", "preferences"]);
export type ProfileSection = z.infer<typeof ProfileSectionSchema>;

export const ProfileSectionPayloadSchema = z.record(z.string(), z.union([
  z.string().max(500),
  z.number().finite(),
  z.boolean(),
  z.null(),
  z.array(z.string().max(200)).max(50)
])).refine((value) => JSON.stringify(value).length <= 30_000, "Section data is too large.");
export type ProfileSectionPayload = z.infer<typeof ProfileSectionPayloadSchema>;

export const ActionStatusSchema = z.enum(["not-started", "in-progress", "done", "dismissed"]);
export type ActionStatus = z.infer<typeof ActionStatusSchema>;
export const ActionEvidenceModeSchema = z.enum(["fact-updated", "document-checked", "real-world-action", "accepted-risk"]);

export const NudgePreferencesSchema = z.object({
  enabled: z.boolean(),
  emailEnabled: z.boolean(),
  cadence: z.enum(["weekly", "fortnightly", "monthly"]),
  quietHoursStart: z.number().int().min(0).max(23),
  quietHoursEnd: z.number().int().min(0).max(23),
  timezone: z.string().min(1).max(80)
});
export type NudgePreferences = z.infer<typeof NudgePreferencesSchema>;

export const AssessmentRequestSchema = z.object({
  facts: FinancialFactsSchema,
  idempotencyKey: z.string().uuid()
});

export const AssessmentDraftSchema = z.object({
  facts: FinancialFactsSchema,
  step: z.number().int().min(0).max(6)
});
export type AssessmentDraft = z.infer<typeof AssessmentDraftSchema>;

export const GuestImportRequestSchema = z.object({
  facts: FinancialFactsSchema,
  idempotencyKey: z.string().uuid(),
  consent: z.object({
    version: z.literal("2026-08-30"),
    acceptedAt: z.string().datetime()
  })
});

export function rupeesToPaise(rupees: number): MoneyPaise {
  return MoneyPaiseSchema.parse(Math.round(rupees * 100));
}

export function paiseToRupees(paise: number): number {
  return Math.round(paise) / 100;
}

// The Paise-named helpers remain for encrypted India-data compatibility.
export function currencyMinorScale(currency: CurrencyCode): number {
  return 10 ** (currencies[currency]?.decimals ?? 2);
}

export function majorToMinor(amount: number, currency: CurrencyCode): MoneyPaise {
  return MoneyPaiseSchema.parse(Math.round(amount * currencyMinorScale(currency)));
}

export function minorToMajor(amountMinor: number, currency: CurrencyCode): number {
  return Math.round(amountMinor) / currencyMinorScale(currency);
}

export function formatMoneyMinor(amountMinor: number, currency: CurrencyCode, locale?: string): string {
  const resolvedLocale = locale ?? COUNTRY_OPTIONS.find((option) => option.currency === currency)?.locale ?? "en";
  return new Intl.NumberFormat(resolvedLocale, { style: "currency", currency, maximumFractionDigits: 0 }).format(minorToMajor(amountMinor, currency));
}
